import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect
} from 'react';
import ToggleButton from '@input/ToggleButton';
import DashedLine from '@components/DashedLine';
import useResizeObserver from '@hooks/useResizeObserver';
import useClickOutside from '@hooks/useClickOutside';
import useEscapeKey from '@hooks/useEscapeKey';
import useMatchMedia from '@hooks/useMatchMedia';
import * as styles from './ProductTypes.module.scss';
import Arrow from '@images/arrow.svg';
import classNames from 'classnames';
import { uniqueId, isBoolean } from 'lodash';
import tracker from '@utils/tracker';

const dropdownPadding = parseInt(styles.dropdownPadding);

export default React.memo(function ProductTypeButtons({
  productCategory,
  isEnabled,
  isLoading,
  isDropdown,
  isModal,
  isHidden,
  hasErrors,
  dropdownTextBreakpoint,
  className,
  categoryClassName,
  categoryText,
  categoryLoadingText,
  categoryTabIndex,
  productClassName,
  products = [],
  showProducts,
  categoryButtonIconSize,
  spinnerSize,
  spinnerThickness,
  onCategoryToggle,
  onProductToggle,
  ...rest
}) {
  const wrapper = useRef();
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const dropdownWrapper = useRef();
  const listId = useMemo(() => uniqueId('product-list-'), []);

  const handleCategoryChange = useCallback(
    isEnabled => {
      onCategoryToggle(productCategory, isEnabled);

      const action = isEnabled
        ? tracker.actions.ENABLE_PRODUCT_TYPE
        : tracker.actions.DISABLE_PRODUCT_TYPE;

      tracker.track(action, {
        product_category: productCategory
      });
    },
    [productCategory, onCategoryToggle]
  );

  const { height: wrapperHeight } = useResizeObserver(wrapper, {
    checkWidth: false
  });

  let productComponents = !showProducts
    ? null
    : products?.map((product, index) => (
      <ProductType
        key={index}
        className={productClassName}
        categoryIsEnabled={isEnabled}
        tabIndex={(isDropdown && !dropdownIsOpen) || hasErrors ? -1 : null}
        onChange={onProductToggle}
        toggleProductCategory={handleCategoryChange}
        hasErrors={hasErrors}
        {...product}
      />
    ));

  const openDropdown = useCallback(() => {
    if (isDropdown) {
      if (dropdownWrapper.current) {
        dropdownWrapper.current.classList.add(styles.scrolling);
        dropdownWrapper.current.scrollTop = 0;

        setTimeout(() => {
          dropdownWrapper.current.classList.remove(styles.scrolling);
        }, 0);
      }

      setDropdownIsOpen(true);
    }
  }, [isDropdown]);

  const closeDropdown = useCallback(
    force => {
      if (isDropdown || (isBoolean(force) && force)) {
        setDropdownIsOpen(false);
      }
    },
    [isDropdown]
  );

  useEffect(() => {
    if (!isDropdown) {
      closeDropdown(true);
    }
  }, [isDropdown, closeDropdown]);

  useClickOutside(wrapper, closeDropdown);
  useEscapeKey(closeDropdown);

  if (productComponents && isDropdown) {
    productComponents = (
      <div
        ref={dropdownWrapper}
        className={classNames('product-dropdown', styles.dropdownWrapper)}
      >
        <div className={styles.dropdownContent}>{productComponents}</div>
      </div>
    );
  }

  return (
    productCategory && (
      <div
        ref={wrapper}
        className={classNames(styles.buttonsWrapper, className, {
          [styles.enabled]: isEnabled,
          [styles.dropdown]: isDropdown,
          [styles.open]: isDropdown && dropdownIsOpen,
          [styles.hidden]: isHidden,
          [styles.hasError]: hasErrors,
          'product-dropdown-open': isDropdown && dropdownIsOpen
        })}
        {...rest}
      >
        <ToggleButton
          className={classNames(styles.productCategory, categoryClassName)}
          isActive={isEnabled}
          text={categoryText}
          loadingText={categoryLoadingText}
          isLoading={isLoading}
          isDisabled={hasErrors}
          tabIndex={!hasErrors ? categoryTabIndex : -1}
          spinnerSize={spinnerSize}
          spinnerThickness={spinnerThickness}
          onChange={handleCategoryChange}
        />
        {productComponents && (
          <>
            {isModal && (
              <DashedLine
                direction="horizontal"
                strokeWidth={1.5}
                className={styles.productCategoryDivider}
              />
            )}
            <DropdownToggle
              visible={isDropdown}
              isOpen={dropdownIsOpen}
              isDisabled={hasErrors}
              categoryText={categoryText}
              tabIndex={!hasErrors ? categoryTabIndex : -1}
              textBreakpoint={dropdownTextBreakpoint}
              listId={listId}
              open={openDropdown}
              close={closeDropdown}
            />
            <div
              id={listId}
              className={classNames(
                'calculator-products-list',
                styles.products,
                { [styles.checklist]: isModal || isDropdown }
              )}
              style={{
                paddingTop:
                  !isDropdown || !wrapperHeight
                    ? null
                    : Math.round(wrapperHeight - 10 + dropdownPadding * 2)
              }}
            >
              {productComponents}
            </div>
          </>
        )}
      </div>
    )
  );
});

const ProductType = React.memo(
  ({
    productTypeID,
    label,
    isEnabled,
    hasErrors,
    className,
    categoryIsEnabled,
    loanType,
    tabIndex,
    onChange,
    toggleProductCategory
  }) => {
    return (
      <button
        aria-pressed={isEnabled && categoryIsEnabled}
        tabIndex={tabIndex}
        className={classNames(styles.productType, className, {
          [styles.active]: isEnabled
        })}
        onClick={() => {
          if (hasErrors) {
            return;
          }

          if (!categoryIsEnabled) {
            toggleProductCategory(loanType, true);
            onChange(productTypeID, true);
          } else {
            onChange(productTypeID);
          }
        }}
      >
        {label}
      </button>
    );
  }
);

const DropdownToggle = React.memo(
  ({
    visible,
    categoryText,
    listId,
    textBreakpoint = 475,
    tabIndex,
    isOpen,
    open,
    close
  }) => {
    const [disableHoverEffect, setDisableHoverEffect] = useState(false);

    const isShortText = useMatchMedia(textBreakpoint);

    const dropdownLabel = useMemo(() => {
      const srLabel = categoryText
        .trim()
        .replace(/s$/i, '')
        .replace(/\bproducts?$/i, '');

      return (
        <span>
          {isShortText ? (
            <>
              <span className="sr-only">Select {srLabel}</span> Products
            </>
          ) : (
            <>
              Select <span className="sr-only">{srLabel}</span> Products
            </>
          )}
        </span>
      );
    }, [categoryText, isShortText]);

    return (
      visible && (
        <button
          className={classNames(styles.dropdownToggle, {
            [styles.disableHover]: disableHoverEffect
          })}
          tabIndex={tabIndex}
          aria-haspopup="listbox"
          aria-controls={listId}
          aria-expanded={isOpen}
          onClick={() => {
            setDisableHoverEffect(true);

            if (isOpen) {
              close();
            } else {
              open();
            }
          }}
          onMouseLeave={() => setDisableHoverEffect(false)}
        >
          {dropdownLabel}
          <span className={styles.arrowWrapper}>
            <Arrow className={classNames(styles.arrow, styles.down)} />
            <Arrow className={classNames(styles.arrow, styles.up)} />
          </span>
        </button>
      )
    );
  }
);
